import * as React from 'react'

import { useQuery } from '@apollo/client/react'
import type { ErrorLike } from '@apollo/client'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import type { ResultOf, VariablesOf } from '~publish/graphql'

/**
 * Default per-channel cap for the mention counts query. Matches the comments
 * counter cap so the "99+" display and the >50 promo-banner threshold both
 * resolve from a single fetch.
 */
export const MENTION_COUNTS_LIMIT = 100

export const MentionCountsQuery = graphql(/* GraphQL */ `
  query MentionCounts($input: EngagementCountsByChannelIdsInput!) {
    engagementCountsByChannelIds(input: $input) {
      counts {
        channelId
        count
        limit
      }
    }
  }
`)

type MentionCountsResult = ResultOf<typeof MentionCountsQuery>
type MentionCountsVariables = VariablesOf<typeof MentionCountsQuery>

export type MentionCountsValues = {
  channelId: string
  count: number
  limit: number
}

type UseChannelMentionCountsProps = {
  /** Channels to count. Callers pass only mention-supported channels. */
  channels: { id: string }[]
  limit?: number
  /**
   * Restrict the count to mentions published in this range. Both bounds are
   * inclusive. Pass an ISO string that is anchored, not recomputed per render:
   * a moving bound is a new set of query variables every time.
   */
  publishedAt?: { start?: string; end?: string }
  /** Skip the fetch entirely (e.g. feature flag off or mentions unavailable). */
  skip?: boolean
}

type UseChannelMentionCountsReturn = {
  counts: MentionCountsValues[] | null
  loading: boolean
  error: ErrorLike | null
  refetch: () => void
}

/**
 * Fetches the number of unanswered (unreplied) mentions per channel via
 * `engagementCountsByChannelIds`.
 *
 * Unlike the comments equivalent (`useChannelCommentCounts`), mentions have no
 * realtime Pusher events, so this hook cannot self-refresh on ingest/reply.
 * It fetches on mount (`cache-and-network`) and exposes `refetch` for callers
 * to invalidate after a mention is replied to or dismissed — that refetch is
 * what clears the dot once every mention is answered.
 */
export function useChannelMentionCounts({
  channels,
  limit = MENTION_COUNTS_LIMIT,
  publishedAt,
  skip = false,
}: UseChannelMentionCountsProps): UseChannelMentionCountsReturn {
  const organizationId = useOrganizationId()

  const channelIds = React.useMemo(
    () => channels.map((channel) => channel.id),
    [channels],
  )

  // Sorted+joined key keeps the Apollo variables (and cache entry) stable when
  // the channels array identity changes but its contents don't.
  const channelIdsKey = [...channelIds].toSorted().join(',')

  const variables: MentionCountsVariables = React.useMemo(
    () => ({
      input: {
        organizationId: organizationId ?? '',
        engagementType: 'mention',
        channelIds,
        filters: {
          status: 'unreplied',
          ...(publishedAt && { publishedAt }),
        },
        limit,
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      organizationId,
      channelIdsKey,
      limit,
      publishedAt?.start,
      publishedAt?.end,
    ],
  )

  // Skipping puts the query on Apollo's `standby` policy, which stops it
  // fetching but leaves the last result in place. The counts have to be gated
  // on the same condition or they outlive the scope they were fetched for.
  const shouldSkip = skip || !organizationId || channelIds.length === 0

  const { data, loading, error, refetch } = useQuery(MentionCountsQuery, {
    variables,
    fetchPolicy: 'cache-and-network',
    skip: shouldSkip,
  })

  return React.useMemo<UseChannelMentionCountsReturn>(() => {
    const rawCounts: MentionCountsResult['engagementCountsByChannelIds']['counts'] =
      data?.engagementCountsByChannelIds.counts ?? []

    return {
      counts:
        data && !shouldSkip
          ? rawCounts.map((c) => ({
              channelId: c.channelId,
              count: c.count,
              limit: c.limit,
            }))
          : null,
      loading,
      error: error ?? null,
      refetch: () => {
        void refetch()
      },
    }
  }, [data, loading, error, refetch, shouldSkip])
}
